import type { Expense } from './types.ts'
import { standVan, vanMij, type Verwerkstand } from './werklijst.ts'

/* ===========================================================================
 *  Wat zie ik in TW10 -- op de telefoon precies wat TW10 Online laat zien
 *
 *  Casper, 23 okt 2026: "je moet er trouwens ook voor zorgen dat dingen
 *  zoals de auditlogs ect, ook op telefoon hetzelfde gaan zijn. Ook de
 *  dingen die je ziet, gezien je nu ook allemaal fotos ziet.... echt
 *  hetgene wat desktop ook heeft, moet je ook doen".
 *
 *  Dit bestand is de regel van TW10 Online, zonder React en zonder server,
 *  zodat hij op drie plekken hetzelfde doet:
 *
 *    de functie tw10mobile   filtert hiermee wat hij teruggeeft (het slot:
 *                            wat de telefoon niet mag zien, krijgt hij niet)
 *    de app tw10mobile       een letterlijke kopie in src/lib/zicht.ts, voor
 *                            de bakken van de To-do, de knoppen en de
 *                            tijdlijn (dev/kopie-nakijken.mjs daar)
 *    TW10 Online             het origineel staat in src/tw10online/b10/
 *                            zicht.ts, stukjes.tsx (geenFactuur, de
 *                            overzichten) en Detail.tsx (de historie); de
 *                            zelftest telefoon-gelijk rekent beide op
 *                            honderden facturen na en stopt als ze één keer
 *                            iets anders zeggen
 *
 *  Waarom geen import van het origineel: dat hangt aan React, de rechten in
 *  de browser (can) en de server-kopie van TW10 Online. Een functie in Deno
 *  kan daar niet bij. Dus dezelfde regels, met de rechten als vijf vlaggen
 *  die de functie in de database opvraagt (heeft_recht), en de zelftest die
 *  bewaakt dat het dezelfde regels blijven.
 *
 *  Eén verschil met TW10 Online, met opzet: daar is dit "wat je ZIET, geen
 *  slot" (een link naar een factuur buiten je route opent gewoon). Op de
 *  telefoon is het wel een slot, want de functie geeft alleen terug wat
 *  hier past -- ook bij het openen van een factuur of een origineel.
 * =========================================================================== */

/* ------------------------------------------------------------------ *
 *  Geen factuur (21 okt 2026) -- woordelijk geenFactuur() uit stukjes.tsx
 *
 *  Casper: "Daarnaast staan er veel dingen zoals persoonlijke fotos bij
 *  valideren?" Plaatjes uit mailhandtekeningen, foto's die de AI-assistent
 *  als "geen stuk" herkende en mails zonder bestand. Ze gaan niet weg, maar
 *  staan niet tussen de facturen. tochFactuur is de mens die het zegt.
 * ------------------------------------------------------------------ */

export function geenFactuur(e: Pick<Expense, 'tochFactuur' | 'gelezen' | 'attachmentPath' | 'amountExcl' | 'source' | 'status' | 'exactId'>): boolean {
  if (e.exactId || e.status === 'goedgekeurd') return false
  if (e.tochFactuur === true) return false
  if (e.tochFactuur === false) return true
  if (e.gelezen?.soort === 'geenstuk') return true
  /* Een mail zonder bestand die niemand las en niets kost: een nieuwsbrief. */
  return e.source === 'mail' && !e.attachmentPath && !e.gelezen && !e.amountExcl
}

/* ------------------------------------------------------------------ *
 *  De rechten: vijf vlaggen in plaats van can(user, ...)
 * ------------------------------------------------------------------ */

export interface Rechten {
  /** De rol management. */
  management: boolean
  /** admin.desk: Boekhouden. */
  boekhouden: boolean
  /** expenses.approve: facturen zien en tekenen. */
  beslissen: boolean
  /** tw10.alles: Alles zien (0236). */
  alles: boolean
  /** tw10.valideren: valideren voor alle bedrijven (0236). */
  valideren: boolean
}

/** Een rij uit tw10_bedrijfsrecht (0240, 0241): per bedrijf vier vinkjes. */
export interface Bedrijfsrecht {
  administratie: string
  controleren: boolean
  boeken: boolean
  afronden: boolean
  valideren: boolean
}

/** Zoals alsRecht() in matrix.ts: een losse code is bekijken (0240). */
export function alsRecht(x: string | (Partial<Bedrijfsrecht> & { administratie: string })): Bedrijfsrecht {
  if (typeof x === 'string') return { administratie: x, controleren: true, boeken: false, afronden: false, valideren: false }
  return {
    administratie: x.administratie,
    controleren: x.controleren ?? true,
    boeken: !!x.boeken,
    afronden: !!x.afronden,
    valideren: !!x.valideren,
  }
}

const iets = (r: Bedrijfsrecht): boolean => !!(r.controleren || r.boeken || r.afronden || r.valideren)
const metVinkje = (lijst: readonly Bedrijfsrecht[], v: keyof Omit<Bedrijfsrecht, 'administratie'>): string[] =>
  lijst.filter((r) => r[v]).map((r) => r.administratie)

/* ------------------------------------------------------------------ *
 *  Zicht -- zichtVan() uit zicht.ts
 * ------------------------------------------------------------------ */

export interface Zicht {
  alles: boolean
  valideren: boolean
  route: boolean
  bedrijven: string[]
  valideerBedrijven: string[]
  boekBedrijven: string[]
  afrondBedrijven: string[]
}

export function zichtVan(ik: string, r: Pick<Rechten, 'alles' | 'valideren'>, alle: readonly Expense[],
  bedrijfsrechten: readonly (string | Bedrijfsrecht)[] = []): Zicht {
  const rechten = bedrijfsrechten.map(alsRecht).filter(iets)
  return {
    alles: r.alles,
    valideren: r.alles || r.valideren,
    route: r.alles || alle.some((e) => [e.eersteBij, e.goedkeurders, e.afronders].some((g) => g?.includes(ik))),
    bedrijven: rechten.map((x) => x.administratie),
    valideerBedrijven: metVinkje(rechten, 'valideren'),
    boekBedrijven: metVinkje(rechten, 'boeken'),
    afrondBedrijven: metVinkje(rechten, 'afronden'),
  }
}

export function valideertBij(z: Zicht, adm: string | undefined | null): boolean {
  return z.valideren || (!!adm && z.valideerBedrijven.includes(adm))
}

export const valideertIets = (z: Zicht): boolean => z.valideren || z.valideerBedrijven.length > 0

export function boektBij(z: Zicht, adm: string | undefined | null): boolean {
  return !!adm && z.boekBedrijven.includes(adm)
}

/** Met de hand naar Exact: Boekhouden overal, of het vinkje Boeken voor dit bedrijf (0241). */
export function magHandBoeken(r: Pick<Rechten, 'boekhouden'>, z: Zicht, adm: string | undefined | null): boolean {
  return r.boekhouden || boektBij(z, adm)
}

const BOEK_STANDEN: Verwerkstand[] = ['boeken', 'geweigerd']

export function teBoekenVoorMij(e: Expense, z: Zicht, nu = Date.now()): boolean {
  if (e.hoortBij || geenFactuur(e) || (e.richting ?? 'inkoop') !== 'inkoop') return false
  return boektBij(z, e.administratie) && BOEK_STANDEN.includes(standVan(e, nu))
}

export const inDispuut = (e: Pick<Expense, 'dispuutSinds'>): boolean => !!e.dispuutSinds

/** Wacht deze factuur nu op mij? In dispuut staat hij stil (0240). */
export function wachtOpMij(e: Expense, z: Zicht, ik: string, inst: Record<string, string>, nu = Date.now()): boolean {
  if (inDispuut(e)) return false
  return mijnStap(e, z, ik, inst, nu)
}

function mijnStap(e: Expense, z: Zicht, ik: string, inst: Record<string, string>, nu: number): boolean {
  if (e.hoortBij || geenFactuur(e)) return false
  const s = standVan(e, nu)
  if (s === 'lezen' || s === 'vastgelopen') return z.valideren
  if (s === 'valideren' || s === 'aanvullen') return valideertBij(z, e.administratie)
  return vanMij(e, ik, inst, nu)
}

/** Heb ik er zelf al iets mee gedaan (gevalideerd of getekend)? "Eerder gezien". */
export function doorMij(e: Expense, ik: string): boolean {
  return e.gevalideerdDoor === ik || e.eersteDoor === ik || e.tweedeDoor === ik || e.approvedBy === ik
}

export function vanMijnBedrijf(e: Pick<Expense, 'administratie'>, z: Zicht): boolean {
  return !!e.administratie && z.bedrijven.includes(e.administratie)
}

/** Mag ik deze factuur zien? Alles zien: alles. Anders: mijn stap, wat ik deed, mijn dispuut, mijn bedrijven. */
export function magZien(e: Expense, z: Zicht, ik: string, inst: Record<string, string>, nu = Date.now()): boolean {
  if (z.alles) return true
  if (e.hoortBij) return false
  return mijnStap(e, z, ik, inst, nu) || doorMij(e, ik) || e.dispuutDoor === ik || vanMijnBedrijf(e, z)
}

const DAG = 86_400_000

/** Reeds vervallen, of vervalt binnen zeven dagen -- en nog niet betaald of afgekeurd. */
export function vervaltBinnenkort(e: Expense, nu = Date.now()): boolean {
  if (!e.vervaldatum || e.betaaldAt || e.status === 'afgekeurd' || geenFactuur(e)) return false
  return e.vervaldatum <= nu + 7 * DAG
}

/* ------------------------------------------------------------------ *
 *  De overzichten: zonder Alles zien (zicht.ts), met (stukjes.tsx)
 * ------------------------------------------------------------------ */

export type EigenKeuze = 'todo' | 'vervalt' | 'dispuut' | 'gedaan' | 'bedrijven' | 'boeken' | 'zichtbaar'

export const EIGEN_OVERZICHTEN: { waarde: EigenKeuze; label: string }[] = [
  { waarde: 'todo', label: 'To-do' },
  { waarde: 'vervalt', label: 'Reeds vervallen + 7 dagen' },
  { waarde: 'dispuut', label: 'In dispuut' },
  { waarde: 'gedaan', label: 'Eerder gezien' },
  { waarde: 'bedrijven', label: 'Mijn bedrijven' },
  { waarde: 'boeken', label: 'Te boeken (mijn bedrijven)' },
  { waarde: 'zichtbaar', label: 'Alles wat ik zie' },
]

export function eigenOverzichten(z: Zicht): { waarde: EigenKeuze; label: string }[] {
  return EIGEN_OVERZICHTEN.filter((o) => (o.waarde !== 'bedrijven' || z.bedrijven.length > 0)
    && (o.waarde !== 'boeken' || z.boekBedrijven.length > 0))
}

export function pastBijEigen(e: Expense, k: EigenKeuze, z: Zicht, ik: string, inst: Record<string, string>, nu = Date.now()): boolean {
  if (e.hoortBij) return false
  switch (k) {
    case 'todo': return wachtOpMij(e, z, ik, inst, nu)
    case 'vervalt': return vervaltBinnenkort(e, nu) && magZien(e, z, ik, inst, nu)
    case 'dispuut': return inDispuut(e) && magZien(e, z, ik, inst, nu)
    case 'gedaan': return doorMij(e, ik)
    case 'bedrijven': return vanMijnBedrijf(e, z) && !geenFactuur(e)
    case 'boeken': return teBoekenVoorMij(e, z, nu)
    case 'zichtbaar': return magZien(e, z, ik, inst, nu) && !geenFactuur(e)
  }
}

export type Overzicht = 'todo' | 'importeren' | 'valideren' | 'goedkeuren' | 'boeken' | 'fout' | 'geboekt' | 'betalen' | 'betaald' | 'afgekeurd' | 'geenfactuur' | 'dispuut' | 'alles'

export const OVERZICHTEN: { waarde: Overzicht; label: string }[] = [
  { waarde: 'todo', label: 'To-do' },
  { waarde: 'importeren', label: 'Importeren' },
  { waarde: 'valideren', label: 'Te valideren' },
  { waarde: 'goedkeuren', label: 'Goed te keuren' },
  { waarde: 'boeken', label: 'Te boeken' },
  { waarde: 'fout', label: 'Fout' },
  { waarde: 'geboekt', label: 'Geboekt' },
  { waarde: 'betalen', label: 'Te betalen' },
  { waarde: 'betaald', label: 'Betaald' },
  { waarde: 'afgekeurd', label: 'Afgekeurd' },
  { waarde: 'geenfactuur', label: 'Geen factuur' },
  { waarde: 'dispuut', label: 'In dispuut' },
  { waarde: 'alles', label: 'Alles' },
]

const BIJ_OVERZICHT: Record<Exclude<Overzicht, 'todo' | 'alles' | 'geenfactuur' | 'dispuut'>, Verwerkstand[]> = {
  importeren: ['lezen', 'vastgelopen'],
  valideren: ['aanvullen', 'valideren'],
  goedkeuren: ['akkoord', 'tweede', 'afronden'],
  boeken: ['boeken', 'geweigerd'],
  fout: ['geweigerd', 'vastgelopen'],
  geboekt: ['betalen', 'klaar'],
  betalen: ['betalen'],
  betaald: ['klaar'],
  afgekeurd: ['afgekeurd'],
}

const TODO: Verwerkstand[] = ['lezen', 'vastgelopen', 'aanvullen', 'valideren', 'akkoord', 'tweede', 'afronden', 'boeken', 'geweigerd']

export function pastBijOverzicht(bon: Expense, o: Overzicht, nu = Date.now()): boolean {
  if (o === 'alles') return true
  if (o === 'geenfactuur') return geenFactuur(bon) && bon.status !== 'afgekeurd'
  if (o === 'dispuut') return !!bon.dispuutSinds
  if (geenFactuur(bon)) return false
  const s = standVan(bon, nu)
  if (o === 'todo') return TODO.includes(s) && !bon.dispuutSinds
  return BIJ_OVERZICHT[o].includes(s)
}

/** Een keuze uit een van beide lijsten. */
export type Keuze = Overzicht | EigenKeuze

const EIGEN: readonly string[] = EIGEN_OVERZICHTEN.map((o) => o.waarde)
const ALLE: readonly string[] = OVERZICHTEN.map((o) => o.waarde)

/** Zoals Overzicht.tsx: wat niet bij jou past, is To-do; "Alles wat ik zie" is met Alles zien gewoon Alles. */
export const eigenKeuze = (k: string): EigenKeuze => (EIGEN.includes(k) ? k as EigenKeuze : 'todo')
export const alleKeuze = (k: string): Overzicht =>
  (k === 'zichtbaar' ? 'alles' : k === 'vervalt' || k === 'gedaan' || k === 'bedrijven' ? 'todo' : ALLE.includes(k) ? k as Overzicht : 'todo')

/** De keuzes die bij mij horen: met Alles zien die van Blue10, anders de eigen. */
export function overzichtenVoor(z: Zicht): { waarde: Keuze; label: string }[] {
  return z.alles ? OVERZICHTEN : eigenOverzichten(z)
}

/** Past deze factuur bij deze keuze, voor mij? De regel van de lijst in Overzicht.tsx (hoort_bij eruit). */
export function pastBijKeuze(e: Expense, k: string, z: Zicht, ik: string, inst: Record<string, string>, nu = Date.now()): boolean {
  if (e.hoortBij) return false
  return z.alles ? pastBijOverzicht(e, alleKeuze(k), nu) : pastBijEigen(e, eigenKeuze(k), z, ik, inst, nu)
}

/* ------------------------------------------------------------------ *
 *  In dispuut: wie mag erin zetten en eruit halen (zicht.ts)
 *
 *  Wat factuur_dispuut() in de database zegt (0240), zo goed als het hier
 *  kan; de database heeft het laatste woord.
 * ------------------------------------------------------------------ */

const boekhoudt = (r: Rechten): boolean => r.management || r.boekhouden || r.alles

export function magDispuutZetten(e: Expense, ik: string, r: Rechten): boolean {
  if (inDispuut(e) || e.hoortBij || e.status === 'afgekeurd' || e.betaaldAt) return false
  if (!r.management && !r.beslissen) return false
  if (boekhoudt(r)) return true
  const route = [...(e.eersteBij ?? []), ...(e.goedkeurders ?? []), ...(e.afronders ?? [])]
  return route.includes(ik) || doorMij(e, ik) || (r.valideren && e.status === 'open')
}

export function magUitDispuut(e: Expense, ik: string, r: Rechten): boolean {
  if (!inDispuut(e)) return false
  if (!r.management && !r.beslissen) return false
  return e.dispuutDoor === ik || boekhoudt(r)
}

/* ------------------------------------------------------------------ *
 *  De historie: dezelfde woorden als Detail.tsx in TW10 Online
 *
 *  Casper (21 okt 2026): "mobiele versie staat nu niet gelijk aan desktop
 *  versie, qua logs ect". HISTORIE en VELD zijn woordelijk die van
 *  Detail.tsx; de zelftest legt ze naast elkaar. exact_weigerde staat er
 *  hier bij: Detail.tsx kent hem (nog) niet en zet dan de kale sleutel neer.
 * ------------------------------------------------------------------ */

export const HISTORIE: Record<string, string> = {
  aangemaakt: 'Binnengekomen',
  gewijzigd: 'Gewijzigd',
  gevalideerd: 'Gevalideerd',
  eerste_akkoord: 'Akkoord',
  tweede_akkoord: 'Akkoord',
  goedgekeurd: 'Goedgekeurd',
  afgekeurd: 'Afgekeurd',
  heropend: 'Teruggezet',
  notitie: 'Opmerking',
  naar_exact: 'Geboekt',
  dispuut: 'In dispuut gezet',
  uit_dispuut: 'Uit dispuut gehaald',
  exact_weigerde: 'Exact weigerde',
}

export const VELD: Record<string, string> = {
  amount_excl: 'bedrag', grootboek_code: 'grootboekrekening', supplier: 'leverancier', factuurnummer: 'factuurnummer',
  administratie: 'bedrijf', betaal_iban: 'rekeningnummer', description: 'omschrijving', expense_date: 'factuurdatum',
  vervaldatum: 'vervaldatum', vat_pct: 'btw', valuta: 'valuta', betaalconditie: 'betalingsconditie', btw_bedrag: 'btw-bedrag',
}

/*
 * Nooit tonen: rekeningnummer, btw-nummer, KvK (Casper, 20 okt 2026: die
 * komen uit Exact). Detail.tsx noemt de kolomnamen; de trigger op expenses
 * (0094) schrijft een gewijzigd rekeningnummer echter weg als veld
 * 'rekeningnummer' (zo staan ze in de database), en daarom staat die hier ook.
 */
export const VERBORGEN: readonly string[] = ['betaal_iban', 'btw_nummer', 'kvk', 'rekeningnummer']

export interface Gebeurtenis { soort: string; veld?: string | null; tekst?: string | null; oud?: string | null; nieuw?: string | null; doorNaam?: string | null }

export const verborgen = (g: Pick<Gebeurtenis, 'soort' | 'veld'>): boolean =>
  g.soort === 'gewijzigd' && !!g.veld && VERBORGEN.includes(g.veld)

/** De kop van een regel in de historie: "bedrag gewijzigd", "Akkoord", ... */
export function historieWat(g: Pick<Gebeurtenis, 'soort' | 'veld'>): string {
  return g.soort === 'gewijzigd' && g.veld ? `${VELD[g.veld] ?? g.veld} gewijzigd` : HISTORIE[g.soort] ?? g.soort
}

/** Wat eronder staat: "oud → nieuw" bij een wijziging, anders de tekst (niet bij gevalideerd en goedgekeurd). */
export function historieTekst(g: Gebeurtenis): string | null {
  if (g.soort === 'gewijzigd' && (g.oud || g.nieuw)) return `${g.oud || '—'} → ${g.nieuw || '—'}`
  if (g.tekst && g.soort !== 'gevalideerd' && g.soort !== 'goedgekeurd') return g.tekst
  return null
}
